/**
 * Media downloader — fetches files from remote URLs (R2/CDN) to the
 * project's local public/media/ folder so Remotion can use them.
 */

import { promises as fs } from 'node:fs';
import path from 'node:path';
import { pipeline } from 'node:stream/promises';
import { createWriteStream } from 'node:fs';
import type { ProjectManager } from './remotion/project.js';

export interface DownloadedMedia {
  filename: string;
  relativePath: string;
  absolutePath: string;
  sizeBytes: number;
  mimeType: string;
}

/**
 * Downloads a file from a URL to the project's media folder.
 * Generates a unique filename based on the URL.
 */
export async function downloadMedia(
  project: ProjectManager,
  url: string,
  onProgress?: (downloaded: number, total: number | null) => void,
): Promise<DownloadedMedia> {
  const urlObj = new URL(url);
  const originalName = path.basename(urlObj.pathname) || 'media';
  const ext = path.extname(originalName) || guessExtFromUrl(url);
  const filename = `media-${Date.now()}-${Math.random().toString(36).slice(2, 8)}${ext}`;
  const absolutePath = project.mediaPath(filename);
  const relativePath = project.mediaRelativePath(filename);

  const response = await fetch(url, { signal: AbortSignal.timeout(120_000) });
  if (!response.ok) {
    throw new Error(`Download failed: HTTP ${response.status} for ${url}`);
  }

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? Number(contentLength) : null;
  const mimeType = response.headers.get('content-type')?.split(';')[0]?.trim() || 'application/octet-stream';

  if (!response.body) {
    throw new Error('Download failed: no response body');
  }

  // Stream to file
  const writer = createWriteStream(absolutePath);
  const reader = response.body.getReader();
  let downloaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    downloaded += value.length;
    onProgress?.(downloaded, total);
    writer.write(value);
  }
  writer.end();

  await new Promise<void>((resolve, reject) => {
    writer.on('finish', resolve);
    writer.on('error', reject);
  });

  const stat = await fs.stat(absolutePath);

  return {
    filename,
    relativePath,
    absolutePath,
    sizeBytes: stat.size,
    mimeType,
  };
}

/**
 * Probes video duration (in seconds) using ffprobe if available,
 * otherwise falls back to a default based on file size.
 * Returns duration in seconds.
 */
export async function probeVideoDuration(filePath: string): Promise<number | null> {
  try {
    const { spawn } = await import('node:child_process');
    return new Promise((resolve) => {
      const ffprobe = spawn('ffprobe', [
        '-v', 'error',
        '-show_entries', 'format=duration',
        '-of', 'default=noprint_wrappers=1:nokey=1',
        filePath,
      ], { stdio: ['ignore', 'pipe', 'ignore'] });

      let output = '';
      ffprobe.stdout.on('data', (data: Buffer) => {
        output += data.toString();
      });

      ffprobe.on('close', () => {
        const duration = parseFloat(output.trim());
        resolve(isNaN(duration) ? null : duration);
      });

      ffprobe.on('error', () => resolve(null));
    });
  } catch {
    return null;
  }
}

/**
 * Probes video FPS using ffprobe if available.
 * Returns FPS as a number (e.g. 24, 30, 60), or null if not available.
 */
export async function probeVideoFps(filePath: string): Promise<number | null> {
  try {
    const { spawn } = await import('node:child_process');
    return new Promise((resolve) => {
      const ffprobe = spawn('ffprobe', [
        '-v', 'error',
        '-select_streams', 'v:0',
        '-show_entries', 'stream=r_frame_rate',
        '-of', 'default=noprint_wrappers=1:nokey=1',
        filePath,
      ], { stdio: ['ignore', 'pipe', 'ignore'] });

      let output = '';
      ffprobe.stdout.on('data', (data: Buffer) => {
        output += data.toString();
      });

      ffprobe.on('close', () => {
        const raw = output.trim();
        // ffprobe returns fraction like "24/1" or "30000/1001"
        const match = raw.match(/^(\d+)\/(\d+)$/);
        if (match) {
          const fps = parseInt(match[1]) / parseInt(match[2]);
          resolve(Math.round(fps * 100) / 100);
        } else {
          const fps = parseFloat(raw);
          resolve(isNaN(fps) ? null : fps);
        }
      });

      ffprobe.on('error', () => resolve(null));
    });
  } catch {
    return null;
  }
}

function guessExtFromUrl(url: string): string {
  const lower = url.toLowerCase().split('?')[0];
  if (lower.endsWith('.mp4')) return '.mp4';
  if (lower.endsWith('.webm')) return '.webm';
  if (lower.endsWith('.mov')) return '.mov';
  if (lower.endsWith('.mp3')) return '.mp3';
  if (lower.endsWith('.wav')) return '.wav';
  if (lower.endsWith('.m4a')) return '.m4a';
  if (lower.endsWith('.png')) return '.png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return '.jpg';
  if (lower.endsWith('.webp')) return '.webp';
  return '';
}
